import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { storesApi } from '../../api/endpoints';
import { useAuthStore } from '../../stores/auth.store';
import type { Shop } from '@motra/shared';

export default function StoreSettings() {
  const { t } = useTranslation();
  const shop = useAuthStore((s) => s.shop);
  const setShop = useAuthStore((s) => s.setShop);

  const [form, setForm] = useState<Partial<Shop>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (shop) {
      setForm({ ...shop });
    }
  }, [shop]);

  function setField<K extends keyof Shop>(key: K, value: Shop[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!shop?.id) return;
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const res = await storesApi.update(shop.id, form);
      if (res.success && res.data) {
        setShop(res.data);
        setSuccess(true);
      } else {
        setError(res.error ?? 'Update failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving settings');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('settings', 'Store Settings')}</h1>
        <span className="page-subtitle">{shop?.name ?? shop?.id}</span>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <h3 className="form-section-title">Printer Settings</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Printer IP Address</label>
            <input
              type="text"
              className="form-input"
              value={form.ip_address ?? ''}
              onChange={(e) => setField('ip_address', e.target.value)}
              placeholder="192.168.1.100"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Printer Port</label>
            <input
              type="text"
              className="form-input"
              value={form.printer_port ?? ''}
              onChange={(e) => setField('printer_port', e.target.value)}
              placeholder="9100"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Printer Width (chars)</label>
            <input
              type="text"
              className="form-input"
              value={form.printer_width ?? ''}
              onChange={(e) => setField('printer_width', e.target.value)}
              placeholder="42"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Receipt Copies</label>
            <input
              type="number"
              className="form-input"
              value={form.receipt_copies ?? 1}
              onChange={(e) => setField('receipt_copies', parseInt(e.target.value) || 1)}
              min={1}
              max={5}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Secondary Printer IP</label>
            <input
              type="text"
              className="form-input"
              value={form.secondary_printer ?? ''}
              onChange={(e) => setField('secondary_printer', e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Receipt Format</label>
            <select
              className="form-select"
              value={form.receipt_format ?? 'standard'}
              onChange={(e) => setField('receipt_format', e.target.value)}
            >
              <option value="standard">Standard</option>
              <option value="compact">Compact</option>
              <option value="detailed">Detailed</option>
            </select>
          </div>
        </div>

        <h3 className="form-section-title">Store Options</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('currency', 'Currency Symbol')}</label>
            <input
              type="text"
              className="form-input"
              value={form.curr_sym ?? ''}
              onChange={(e) => setField('curr_sym', e.target.value)}
              placeholder="$"
              maxLength={5}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Offline Mode</label>
            <select
              className="form-select"
              value={form.offline_toggle ?? 'off'}
              onChange={(e) => setField('offline_toggle', e.target.value)}
            >
              <option value="off">Off</option>
              <option value="on">On</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Print Settlement</label>
            <select
              className="form-select"
              value={form.print_settlement ?? 'yes'}
              onChange={(e) => setField('print_settlement', e.target.value)}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        </div>

        {error && <div className="alert alert--error">{error}</div>}
        {success && <div className="alert alert--success">Settings saved successfully.</div>}

        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? 'Saving...' : t('save', 'Save Settings')}
          </button>
        </div>
      </form>
    </div>
  );
}
